/**
 * ScamBlocker AI — Fatura & Abonelik Sayfası (/billing)
 * Plan bilgisi, kullanım kotası, fatura geçmişi.
 */

import { useQuery } from '@tanstack/react-query';
import { CheckCircle, CreditCard, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import apiClient from '@/shared/lib/api/client';
import { API } from '@/shared/lib/api/endpoints';
import styles from './BillingPage.module.css';

interface BillingInfo {
  plan_code: string;
  plan_name: string;
  status: 'active' | 'trial' | 'expired' | 'canceled';
  current_period_end?: string;
  trial_end?: string;
  source: string;
  usage: {
    calls_this_month: number;
    calls_limit: number | null;
    ai_checks_this_month: number;
    ai_checks_limit: number | null;
    seats_used: number;
    seats_limit: number | null;
  };
  invoices?: Invoice[];
}

interface Invoice {
  id: string;
  amount: number;
  currency: string;
  status: 'paid' | 'open' | 'void';
  period_start: string;
  period_end: string;
  pdf_url?: string;
}

const STATUS_LABEL_KEY: Record<string, string> = {
  active: 'billing.statusActive',
  trial: 'billing.statusTrial',
  expired: 'billing.statusExpired',
  canceled: 'billing.statusCanceled',
};

const STATUS_COLOR: Record<string, string> = {
  active: 'var(--color-success)',
  trial: 'var(--color-warning)',
  expired: 'var(--color-danger)',
  canceled: 'var(--color-muted)',
};

function UsageBar({ used, limit }: { used: number; limit: number | null }) {
  const { t } = useTranslation();
  if (limit === null) return <span className={styles.unlimited}>{t('billing.unlimited')}</span>;
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const color =
    pct >= 90 ? 'var(--color-danger)' : pct >= 70 ? 'var(--color-warning)' : 'var(--color-primary)';
  return (
    <div className={styles.usageRow}>
      <span className={styles.usageNums}>
        {used.toLocaleString('tr-TR')} / {limit.toLocaleString('tr-TR')}
      </span>
      <div className={styles.bar}>
        <div className={styles.barFill} style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className={styles.usagePct}>{pct}%</span>
    </div>
  );
}

export function BillingPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery<BillingInfo>({
    queryKey: ['billing'],
    queryFn: async () => {
      const { data } = await apiClient.get(API.org.billing);
      return data;
    },
  });

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <CreditCard size={22} className={styles.titleIcon} />
          <h1 className={styles.title}>{t('billing.pageTitle')}</h1>
        </div>
      </div>

      {isLoading && (
        <div className={styles.center}>
          <span className={styles.spinner} />
        </div>
      )}

      {isError && (
        <div className={styles.errorBox}>Fatura bilgisi yüklenemedi. Lütfen sayfayı yenileyin.</div>
      )}

      {isError && <div className={styles.errorBox}>{t('billing.loadError')}</div>}

      {data && (
        <>
          {/* Plan kartı */}
          <div className={styles.planCard}>
            <div className={styles.planTop}>
              <div>
                <h2 className={styles.planName}>{data.plan_name}</h2>
                <span
                  className={styles.statusBadge}
                  style={{
                    color: STATUS_COLOR[data.status],
                    borderColor: STATUS_COLOR[data.status],
                    background: `${STATUS_COLOR[data.status]}18`,
                  }}
                >
                  {STATUS_LABEL_KEY[data.status]
                    ? t(STATUS_LABEL_KEY[data.status] as string)
                    : data.status}
                </span>
              </div>
              <div className={styles.planMeta}>
                {data.current_period_end && (
                  <span className={styles.periodTxt}>
                    {t('billing.periodEnd', {
                      date: new Date(data.current_period_end).toLocaleDateString('tr-TR'),
                    })}
                  </span>
                )}
                {data.trial_end && data.status === 'trial' && (
                  <span className={styles.trialTxt}>
                    {t('billing.trialEnd', {
                      date: new Date(data.trial_end).toLocaleDateString('tr-TR'),
                    })}
                  </span>
                )}
                <span className={styles.sourceTxt}>
                  {t('billing.source', { source: data.source })}
                </span>
              </div>
            </div>
          </div>

          {/* Kullanım */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <TrendingUp size={16} /> {t('billing.usageThisMonth')}
            </h3>
            <div className={styles.usageGrid}>
              <div className={styles.usageItem}>
                <span className={styles.usageLabel}>{t('billing.usageCalls')}</span>
                <UsageBar used={data.usage.calls_this_month} limit={data.usage.calls_limit} />
              </div>
              <div className={styles.usageItem}>
                <span className={styles.usageLabel}>AI Analiz</span>
                <UsageBar
                  used={data.usage.ai_checks_this_month}
                  limit={data.usage.ai_checks_limit}
                />
              </div>
              <div className={styles.usageItem}>
                <span className={styles.usageLabel}>{t('billing.usageSeats')}</span>
                <UsageBar used={data.usage.seats_used} limit={data.usage.seats_limit} />
              </div>
            </div>
          </section>

          {/* Fatura geçmişi */}
          {data.invoices && data.invoices.length > 0 && (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <CheckCircle size={16} /> {t('billing.invoiceHistory')}
              </h3>
              <div className={styles.invoiceList}>
                {data.invoices.map((inv) => (
                  <div key={inv.id} className={styles.invoiceRow}>
                    <span className={styles.invPeriod}>
                      {new Date(inv.period_start).toLocaleDateString('tr-TR')} –{' '}
                      {new Date(inv.period_end).toLocaleDateString('tr-TR')}
                    </span>
                    <span className={styles.invAmount}>
                      {(inv.amount / 100).toLocaleString('tr-TR', {
                        style: 'currency',
                        currency: inv.currency.toUpperCase(),
                      })}
                    </span>
                    <span
                      className={styles.invStatus}
                      style={{
                        color:
                          inv.status === 'paid' ? 'var(--color-success)' : 'var(--color-warning)',
                      }}
                    >
                      {inv.status === 'paid'
                        ? 'Ödendi'
                        : inv.status === 'open'
                          ? 'Bekliyor'
                          : 'İptal'}
                    </span>
                    {inv.pdf_url && (
                      <a
                        href={inv.pdf_url}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.invPdf}
                      >
                        PDF ↗
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Plan yükseltme CTA */}
          {(data.plan_code === 'free' || data.plan_code === 'starter') && (
            <div className={styles.upgradeBanner}>
              <p className={styles.upgradeText}>{t('billing.upgradeText')}</p>
              <a
                href="mailto:sales@scamblocker.ai?subject=Plan Yükseltme Talebi"
                className={styles.upgradeBtn}
              >
                {t('billing.upgradeContactSales')}
              </a>
            </div>
          )}
        </>
      )}
    </div>
  );
}
